import {useEffect, useRef, useState} from 'react';
import {
    AddPaymentInfoCommandInput,
    AddToCartCommandInput,
    ConversionApiCommandInput,
    EventsTrackingClientConfig,
    EventTrackingService,
    IdentifyUserCommandInput,
    InitiateCheckoutCommandInput,
    PromotionCodeUsedCommandInput,
    PurchaseCommandInput,
    RemoveFromCartCommandInput,
    SearchCommandInput,
    SignInCommandInput,
    SignUpCommandInput,
    TrackingTargetProviderEnum,
    ViewContentCommandInput,
    FestivalFormStartCommandInput,
    FestivalSuccessCommandInput,
} from '@kidslabs/mario-client-event-tracking';

function getServiceConfig(eventTarget: TrackingTargetProviderEnum, window: any): EventsTrackingClientConfig | null {
    const config: EventsTrackingClientConfig = {
        eventTarget,
        cdp: {
            web_event: undefined,
            helper: undefined,
        },
    };

    switch (eventTarget) {
        case TrackingTargetProviderEnum.FACEBOOK_PIXEL:
            if (!window.fbq) return null;
            config.fbq = window.fbq;
            return config;
        case TrackingTargetProviderEnum.FACEBOOK_CONVERSION:
            return config;
        case TrackingTargetProviderEnum.GOOGLE_ANALYTICS:
            if (!window.gtag) return null;
            config.gtag = window.gtag;
            return config;
        case TrackingTargetProviderEnum.CDP:
            if (!window.web_event || !window._cdpEventFunction) return null;
            config.cdp.web_event = window.web_event;
            config.cdp.helper = window._cdpEventFunction;
            return config;
        default:
            return null;
    }
}

function useEventTrackingService(eventTarget: TrackingTargetProviderEnum) {
    const [eventTrackingService, setEventTrackingService] = useState<EventTrackingService | null>(null);
    const eventQueue = useRef<Array<(service: EventTrackingService) => Promise<void>>>([]);

    useEffect(() => {
        const checkFbqInterval = setInterval(() => {
            const config = getServiceConfig(eventTarget, window);

            if (config) {
                clearInterval(checkFbqInterval);
                const service = new EventTrackingService(config);
                setEventTrackingService(service);

                // Process the event queue when eventTrackingService is ready
                eventQueue.current.forEach(async event => {
                    await event(service);
                });
                eventQueue.current = [];
            }
        }, 100); // Check every 100ms

        return () => {
            clearInterval(checkFbqInterval);
        };
    }, []);

    const trackPurchaseEvent = async (input: PurchaseCommandInput) => {
        if (eventTrackingService?.doTrackPurchaseEvent) {
            await eventTrackingService.doTrackPurchaseEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackPurchaseEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackViewContentEvent = async (input: ViewContentCommandInput) => {
        if (eventTrackingService?.doTrackViewContentEvent) {
            await eventTrackingService.doTrackViewContentEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackViewContentEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackAddToCartEvent = async (input: AddToCartCommandInput) => {
        if (eventTrackingService?.doTrackAddToCartEvent) {
            await eventTrackingService.doTrackAddToCartEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackAddToCartEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackRemoveFromCartEvent = async (input: RemoveFromCartCommandInput) => {
        if (eventTrackingService?.doTrackRemoveFromCartEvent) {
            await eventTrackingService.doTrackRemoveFromCartEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackRemoveFromCartEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackSearchEvent = async (input: SearchCommandInput) => {
        if (eventTrackingService?.doTrackSearchEvent) {
            await eventTrackingService.doTrackSearchEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackSearchEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackInitiateCheckoutEvent = async (input: InitiateCheckoutCommandInput) => {
        if (eventTrackingService?.doTrackInitiateCheckoutEvent) {
            await eventTrackingService.doTrackInitiateCheckoutEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackInitiateCheckoutEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackAddPaymentInfoEvent = async (input: AddPaymentInfoCommandInput) => {
        if (eventTrackingService?.doTrackAddPaymentInfoEvent) {
            await eventTrackingService.doTrackAddPaymentInfoEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackAddPaymentInfoEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackSignUpEvent = async (input: SignUpCommandInput) => {
        if (eventTrackingService?.doTrackSignUpEvent) {
            await eventTrackingService.doTrackSignUpEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackSignUpEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackSignInEvent = async (input: SignInCommandInput) => {
        if (eventTrackingService?.doTrackSignInEvent) {
            await eventTrackingService.doTrackSignInEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackSignInEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackIdentifyUserEvent = async (input: IdentifyUserCommandInput) => {
        if (eventTrackingService?.doTrackIdentifyUserEvent) {
            await eventTrackingService.doTrackIdentifyUserEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackIdentifyUserEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackPromotionCodeUsedEvent = async (input: PromotionCodeUsedCommandInput) => {
        if (eventTrackingService?.doTrackPromotionCodeUsedEvent) {
            await eventTrackingService.doTrackPromotionCodeUsedEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackPromotionCodeUsedEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackEventByConversionApi = async (input: ConversionApiCommandInput) => {
        if (eventTrackingService?.doTrackEventConversion) {
            await eventTrackingService.doTrackEventConversion(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackEventConversion(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackFestivalFormStartEvent = async (input: FestivalFormStartCommandInput) => {
        if (eventTrackingService?.doTrackFestivalFormStartEvent) {
            await eventTrackingService.doTrackFestivalFormStartEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackFestivalFormStartEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    const trackFestivalSuccessEvent = async (input: FestivalSuccessCommandInput) => {
        if (eventTrackingService?.doTrackFestivalSuccessEvent) {
            await eventTrackingService.doTrackFestivalSuccessEvent(input);
        } else {
            const trackEvent = async (service: EventTrackingService) => {
                await service.doTrackFestivalSuccessEvent(input);
            };
            // Always add the event to the queue
            eventQueue.current.push(trackEvent);
        }
    };

    return {
        trackPurchaseEvent,
        trackViewContentEvent,
        trackAddToCartEvent,
        trackSearchEvent,
        trackInitiateCheckoutEvent,
        trackAddPaymentInfoEvent,
        trackSignUpEvent,
        trackSignInEvent,
        trackIdentifyUserEvent,
        trackRemoveFromCartEvent,
        trackPromotionCodeUsedEvent,
        trackEventByConversionApi,
        trackFestivalFormStartEvent,
        trackFestivalSuccessEvent,
    };
}

export default useEventTrackingService;
